import { useState, type FormEvent } from 'react';
import { LIMITS, cleanName, type RoomId } from '@engineer-os/chat';
import { useStrings } from '../../state/useLocale';
import { Button } from '../../ui';

/**
 * "Pick a name."
 *
 * The only thing between a learner and the room. One field, one button,
 * and the rule shown only once it has been broken: the protocol's own
 * `cleanName` decides, and the sentence is the protocol's own `nameRule`,
 * asked for in this language through the catalogue, so the prompt never
 * promises something the server then refuses and never explains a refusal in
 * the wrong language.
 *
 * The same form changes a name later. The label and the button change; the
 * rule does not.
 *
 * `inline` is the form in the composer's place, under a conversation the
 * learner is already reading: one row instead of a page, and a button that
 * says "Join" because the room it joins is named at the top of the panel.
 */
export interface NamePromptProps {
  mode: 'join' | 'change';
  room: RoomId;
  initial?: string;
  /** Lay out as one row, to sit where the composer would. */
  inline?: boolean;
  onSubmit(name: string): void;
  onCancel?(): void;
}

export function NamePrompt({ mode, room, initial = '', inline = false, onSubmit, onCancel }: NamePromptProps) {
  const s = useStrings().chat;
  const [value, setValue] = useState(initial);
  const [error, setError] = useState<string | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const name = cleanName(value);
    if (!name) {
      setError(s.rules.name());
      return;
    }
    onSubmit(name);
  };

  return (
    <form className={inline ? 'chat-name is-inline' : 'chat-name'} onSubmit={submit}>
      <label className="label" htmlFor="chat-name-input">
        {mode === 'join' ? s.name.pick : s.name.change}
      </label>
      <input
        id="chat-name-input"
        className="chat-input"
        type="text"
        value={value}
        maxLength={LIMITS.name.max}
        autoComplete="off"
        spellCheck={false}
        autoFocus
        // A name may be written in either script, so the field follows what is
        // typed into it rather than the direction of the page.
        dir="auto"
        aria-describedby="chat-name-note"
        aria-invalid={error !== null}
        onChange={(e) => {
          setValue(e.target.value);
          if (error) setError(null);
        }}
      />
      <p id="chat-name-note" className="note" role={error ? 'alert' : undefined}>
        {error ?? (inline ? s.name.inlineNote : s.name.note)}
      </p>
      <div className="chat-name-actions">
        <Button type="submit" variant="solid">
          {mode === 'join' ? (inline ? s.name.join : s.name.joinRoom(room)) : s.name.save}
        </Button>
        {onCancel && (
          <Button type="button" variant="quiet" onClick={onCancel}>
            {s.name.cancel}
          </Button>
        )}
      </div>
    </form>
  );
}

export default NamePrompt;
